import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { FileText, Ship, Handshake, Plus, Search, TrendingUp, Clock, MapPin, CheckCircle, MessageSquare, Send, Star, Flag } from 'lucide-react';
import { toast } from 'sonner';
import { ConversationalSidebar } from '../components/ConversationalSidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MarketplacePage() {
  const [activeTab, setActiveTab] = useState('rfqs');
  const [loading, setLoading] = useState(false);
  
  // RFQ Management State
  const [rfqs, setRfqs] = useState([]);
  const [showRFQDialog, setShowRFQDialog] = useState(false);
  const [rfqFormData, setRfqFormData] = useState({
    cargo_type: '',
    cargo_quantity_mt: '',
    vessel_class_required: '',
    load_port: '',
    discharge_port: '',
    laycan_start: '',
    laycan_end: '',
    special_requirements: '',
    min_dwt: '',
    max_vessel_age: ''
  });
  
  // Matching State
  const [selectedRFQ, setSelectedRFQ] = useState(null);
  const [matches, setMatches] = useState([]);
  const [shortlistedVessels, setShortlistedVessels] = useState([]);
  const [showMatchesDialog, setShowMatchesDialog] = useState(false);
  
  // Deal Room State
  const [dealRooms, setDealRooms] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [dealMessages, setDealMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showDealDialog, setShowDealDialog] = useState(false);

  const cargoTypes = ['Grain', 'Coal', 'Iron Ore', 'Crude Oil', 'Container'];
  const vesselClasses = ['Bulk Carrier', 'Ore Carrier', 'Open Hatch Carrier', 'Chip Carrier', 'Cement Carrier'];
  const ports = ['Singapore', 'Houston', 'Rotterdam', 'Shanghai', 'Mumbai'];

  useEffect(() => {
    fetchRFQs();
    fetchDealRooms();
    fetchShortlist();
  }, []);

  const fetchShortlist = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/marketplace/shortlist`, {
        withCredentials: true
      });
      setShortlistedVessels(response.data);
    } catch (error) {
      console.error('[Marketplace] Error fetching shortlist:', error);
    }
  };

  const fetchRFQs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/marketplace/rfqs`, {
        withCredentials: true
      });
      setRfqs(response.data);
    } catch (error) {
      console.error('[Marketplace] Error fetching RFQs:', error);
    }
  };

  const fetchDealRooms = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/marketplace/deals`, {
        withCredentials: true
      });
      setDealRooms(response.data);
    } catch (error) {
      console.error('[Marketplace] Error fetching deal rooms:', error);
    }
  };

  const createRFQ = async () => {
    if (!rfqFormData.cargo_type || !rfqFormData.load_port || !rfqFormData.discharge_port) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      // Filter out empty optional fields to avoid 422 errors
      const params = {
        cargo_type: rfqFormData.cargo_type,
        cargo_quantity_mt: rfqFormData.cargo_quantity_mt,
        vessel_class_required: rfqFormData.vessel_class_required,
        load_port: rfqFormData.load_port,
        discharge_port: rfqFormData.discharge_port,
        laycan_start: rfqFormData.laycan_start,
        laycan_end: rfqFormData.laycan_end
      };
      
      // Only add optional fields if they have values
      if (rfqFormData.special_requirements && rfqFormData.special_requirements.trim()) {
        params.special_requirements = rfqFormData.special_requirements;
      }
      if (rfqFormData.min_dwt && rfqFormData.min_dwt !== '') {
        params.min_dwt = parseInt(rfqFormData.min_dwt);
      }
      if (rfqFormData.max_vessel_age && rfqFormData.max_vessel_age !== '') {
        params.max_vessel_age = parseInt(rfqFormData.max_vessel_age);
      }

      await axios.post(`${BACKEND_URL}/api/marketplace/rfqs/create`, null, {
        params: params,
        withCredentials: true
      });
      toast.success('RFQ created successfully!');
      setShowRFQDialog(false);
      setRfqFormData({
        cargo_type: '',
        cargo_quantity_mt: '',
        vessel_class_required: '',
        load_port: '',
        discharge_port: '',
        laycan_start: '',
        laycan_end: '',
        special_requirements: '',
        min_dwt: '',
        max_vessel_age: ''
      });
      await fetchRFQs();
    } catch (error) {
      console.error('[Marketplace] Error creating RFQ:', error);
      toast.error('Failed to create RFQ');
    } finally {
      setLoading(false);
    }
  };

  const generateMatches = async (rfqId) => {
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/marketplace/matches/generate/${rfqId}`, {}, {
        withCredentials: true
      });
      toast.success('Matches generated successfully!');
      await viewMatches(rfqId);
    } catch (error) {
      console.error('[Marketplace] Error generating matches:', error);
      toast.error('Failed to generate matches');
    } finally {
      setLoading(false);
    }
  };

  const viewMatches = async (rfqId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/marketplace/matches/${rfqId}`, {
        withCredentials: true
      });
      setMatches(response.data);
      const rfq = rfqs.find(r => r.id === rfqId);
      setSelectedRFQ(rfq);
      setShowMatchesDialog(true);
    } catch (error) {
      console.error('[Marketplace] Error fetching matches:', error);
      toast.error('Failed to fetch matches');
    } finally {
      setLoading(false);
    }
  };

  const toggleShortlist = async (matchId, shortlisted) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/marketplace/matches/${matchId}/shortlist`, null, {
        params: { shortlisted },
        withCredentials: true
      });
      setMatches(matches.map(m => m.id === matchId ? {...m, shortlisted} : m));
      toast.success(shortlisted ? 'Added to shortlist' : 'Removed from shortlist');
    } catch (error) {
      console.error('[Marketplace] Error updating shortlist:', error);
      toast.error('Failed to update shortlist');
    }
  };

  const createDealRoom = async (rfqId, matchId) => {
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/marketplace/deals/create`, null, {
        params: { rfq_id: rfqId, vessel_match_id: matchId },
        withCredentials: true
      });
      toast.success('Deal room created successfully!');
      await fetchDealRooms();
      setShowMatchesDialog(false);
      setActiveTab('deals');
    } catch (error) {
      console.error('[Marketplace] Error creating deal room:', error);
      toast.error('Failed to create deal room');
    } finally {
      setLoading(false);
    }
  };

  const viewDealRoom = async (dealId) => {
    setLoading(true);
    try {
      const [dealRes, messagesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/marketplace/deals/${dealId}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/marketplace/deals/${dealId}/messages`, { withCredentials: true })
      ]);
      setSelectedDeal(dealRes.data);
      setDealMessages(messagesRes.data);
      setShowDealDialog(true);
    } catch (error) {
      console.error('[Marketplace] Error fetching deal room:', error);
      toast.error('Failed to load deal room');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      await axios.post(`${BACKEND_URL}/api/marketplace/deals/${selectedDeal.id}/messages`, null, {
        params: { message: newMessage },
        withCredentials: true
      });
      const messagesRes = await axios.get(`${BACKEND_URL}/api/marketplace/deals/${selectedDeal.id}/messages`, {
        withCredentials: true
      });
      setDealMessages(messagesRes.data);
      setNewMessage('');
    } catch (error) {
      console.error('[Marketplace] Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  const updateDealStatus = async (dealId, status, stage) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/marketplace/deals/${dealId}/status`, null, {
        params: { status, progress_stage: stage },
        withCredentials: true
      });
      toast.success(`Deal moved to ${status} stage!`);
      await fetchDealRooms();
      
      // Fetch updated deal data to refresh selectedDeal
      if (selectedDeal?.id === dealId) {
        const dealRes = await axios.get(`${BACKEND_URL}/api/marketplace/deals`, {
          withCredentials: true
        });
        const updatedDeal = dealRes.data.find(d => d.id === dealId);
        if (updatedDeal) {
          setSelectedDeal(updatedDeal);
        }
        
        const messagesRes = await axios.get(`${BACKEND_URL}/api/marketplace/deals/${dealId}/messages`, {
          withCredentials: true
        });
        setDealMessages(messagesRes.data);
      }
    } catch (error) {
      console.error('[Marketplace] Error updating deal status:', error);
      toast.error('Failed to update deal status');
    }
  };

  // Helper function to get status name from progress stage
  const getStatusFromStage = (stage) => {
    const stageMap = {
      1: 'negotiation',
      2: 'terms',
      3: 'compliance',
      4: 'closing'
    };
    return stageMap[stage] || 'negotiation';
  };

  const getStatusBadgeColor = (status) => {
    switch(status) {
      case 'submitted': return 'bg-[hsl(var(--info))] text-white';
      case 'matching': return 'bg-[hsl(var(--warning))] text-white';
      case 'quoted': return 'bg-[hsl(var(--success))] text-white';
      case 'negotiation': return 'bg-[hsl(var(--info))] text-white';
      case 'terms': return 'bg-[hsl(var(--warning))] text-white';
      case 'closing': return 'bg-[hsl(var(--success))] text-white';
      case 'completed': return 'bg-[hsl(var(--success))] text-white';
      case 'cancelled': return 'bg-[hsl(var(--muted))] text-white';
      default: return 'bg-[hsl(var(--secondary))]';
    }
  };

  const getFitScoreColor = (score) => {
    if (score >= 85) return 'text-[hsl(var(--success))]';
    if (score >= 70) return 'text-[hsl(var(--info))]';
    if (score >= 50) return 'text-[hsl(var(--warning))]';
    return 'text-[hsl(var(--muted-foreground))]';
  };

  return (
    <div className="space-y-8" data-testid="marketplace-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'var(--font-display)' }}>
          Sagisu Marketplace
        </h1>
        <p className="text-[hsl(var(--muted-foreground))] mt-1">
          RFQ workflows, vessel-cargo matching, and deal closing
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="rfqs" data-testid="tab-rfqs">
            <FileText className="h-4 w-4 mr-2" />
            My RFQs
          </TabsTrigger>
          <TabsTrigger value="shortlist" data-testid="tab-shortlist">
            <Star className="h-4 w-4 mr-2" />
            Shortlist
          </TabsTrigger>
          <TabsTrigger value="matches" data-testid="tab-matches">
            <Search className="h-4 w-4 mr-2" />
            Vessel Matches
          </TabsTrigger>
          <TabsTrigger value="deals" data-testid="tab-deals">
            <Handshake className="h-4 w-4 mr-2" />
            Deal Rooms
          </TabsTrigger>
        </TabsList>

        {/* RFQs Tab */}
        <TabsContent value="rfqs" className="space-y-6">
          <div className="flex justify-between items-center">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Create requests for quote and manage your cargo requirements
            </p>
            <Dialog open={showRFQDialog} onOpenChange={setShowRFQDialog}>
              <DialogTrigger asChild>
                <Button
                  className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                  data-testid="create-rfq-button"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create RFQ
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Request for Quote</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="cargo-type">Cargo Type *</Label>
                      <Select 
                        value={rfqFormData.cargo_type} 
                        onValueChange={(val) => setRfqFormData({...rfqFormData, cargo_type: val})}
                      >
                        <SelectTrigger id="cargo-type" data-testid="cargo-type-select">
                          <SelectValue placeholder="Select cargo" />
                        </SelectTrigger>
                        <SelectContent>
                          {cargoTypes.map((type) => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="cargo-quantity">Cargo Quantity (MT) *</Label>
                      <Input
                        id="cargo-quantity"
                        type="number"
                        value={rfqFormData.cargo_quantity_mt}
                        onChange={(e) => setRfqFormData({...rfqFormData, cargo_quantity_mt: e.target.value})}
                        placeholder="e.g., 50000"
                        data-testid="cargo-quantity-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="vessel-class">Vessel Class Required *</Label>
                    <Select 
                      value={rfqFormData.vessel_class_required} 
                      onValueChange={(val) => setRfqFormData({...rfqFormData, vessel_class_required: val})}
                    >
                      <SelectTrigger id="vessel-class" data-testid="vessel-class-select">
                        <SelectValue placeholder="Select vessel class" />
                      </SelectTrigger>
                      <SelectContent>
                        {vesselClasses.map((cls) => (
                          <SelectItem key={cls} value={cls}>{cls}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="load-port">Load Port *</Label>
                      <Select 
                        value={rfqFormData.load_port} 
                        onValueChange={(val) => setRfqFormData({...rfqFormData, load_port: val})}
                      >
                        <SelectTrigger id="load-port" data-testid="load-port-select">
                          <SelectValue placeholder="Select port" />
                        </SelectTrigger>
                        <SelectContent>
                          {ports.map((port) => (
                            <SelectItem key={port} value={port}>{port}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="discharge-port">Discharge Port *</Label>
                      <Select 
                        value={rfqFormData.discharge_port} 
                        onValueChange={(val) => setRfqFormData({...rfqFormData, discharge_port: val})}
                      >
                        <SelectTrigger id="discharge-port" data-testid="discharge-port-select">
                          <SelectValue placeholder="Select port" />
                        </SelectTrigger>
                        <SelectContent>
                          {ports.map((port) => (
                            <SelectItem key={port} value={port}>{port}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="laycan-start">Laycan Start *</Label>
                      <Input
                        id="laycan-start"
                        type="date"
                        value={rfqFormData.laycan_start}
                        onChange={(e) => setRfqFormData({...rfqFormData, laycan_start: e.target.value})}
                        data-testid="laycan-start-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="laycan-end">Laycan End *</Label>
                      <Input
                        id="laycan-end"
                        type="date"
                        value={rfqFormData.laycan_end}
                        onChange={(e) => setRfqFormData({...rfqFormData, laycan_end: e.target.value})}
                        data-testid="laycan-end-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="min-dwt">Min DWT (optional)</Label>
                      <Input
                        id="min-dwt"
                        type="number"
                        value={rfqFormData.min_dwt}
                        onChange={(e) => setRfqFormData({...rfqFormData, min_dwt: e.target.value})}
                        placeholder="e.g., 50000"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="max-age">Max Vessel Age (optional)</Label>
                      <Input
                        id="max-age"
                        type="number"
                        value={rfqFormData.max_vessel_age}
                        onChange={(e) => setRfqFormData({...rfqFormData, max_vessel_age: e.target.value})}
                        placeholder="e.g., 15"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="special-req">Special Requirements</Label>
                    <Textarea
                      id="special-req"
                      value={rfqFormData.special_requirements}
                      onChange={(e) => setRfqFormData({...rfqFormData, special_requirements: e.target.value})}
                      placeholder="Any specific requirements or notes..."
                      rows={3}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowRFQDialog(false)}>Cancel</Button>
                  <Button 
                    onClick={createRFQ} 
                    disabled={loading}
                    className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                    data-testid="submit-rfq-button"
                  >
                    {loading ? 'Creating...' : 'Create RFQ'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {rfqs.length === 0 ? (
            <Card>
              <CardContent className="py-16">
                <div className="text-center space-y-4">
                  <FileText className="h-12 w-12 text-[hsl(var(--muted-foreground))] mx-auto" />
                  <h3 className="text-lg font-semibold">No RFQs Yet</h3>
                  <p className="text-[hsl(var(--muted-foreground))] max-w-md mx-auto">
                    Click "Create RFQ" to start your first request for quote and find matching vessels.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rfqs.map((rfq) => (
                <Card key={rfq.id} className="hover:shadow-md transition-shadow" data-testid={`rfq-${rfq.id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{rfq.cargo_type}</CardTitle>
                        <p className="text-sm text-[hsl(var(--muted-foreground))]" style={{ fontFamily: 'var(--font-mono)' }}>
                          {rfq.rfq_number}
                        </p>
                      </div>
                      <Badge className={getStatusBadgeColor(rfq.status)} data-testid="rfq-status-badge">
                        {rfq.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-[hsl(var(--muted-foreground))]">Quantity</span>
                        <span className="font-medium">{rfq.cargo_quantity_mt.toLocaleString()} MT</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[hsl(var(--muted-foreground))]">Vessel Class</span>
                        <span className="font-medium">{rfq.vessel_class_required}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[hsl(var(--muted-foreground))]">Route</span>
                        <span className="font-medium">{rfq.load_port} → {rfq.discharge_port}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[hsl(var(--muted-foreground))]">Laycan</span>
                        <span className="font-medium text-xs">
                          {new Date(rfq.laycan_start).toLocaleDateString()} - {new Date(rfq.laycan_end).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    <div className="pt-3 border-t space-y-2">
                      {rfq.status === 'submitted' ? (
                        <Button
                          onClick={() => generateMatches(rfq.id)}
                          disabled={loading}
                          className="w-full"
                          data-testid="generate-matches-button"
                        >
                          <Search className="h-4 w-4 mr-2" />
                          {loading ? 'Generating...' : 'Find Vessels'}
                        </Button>
                      ) : rfq.status === 'matching' ? (
                        <Button
                          onClick={() => viewMatches(rfq.id)}
                          variant="outline"
                          className="w-full"
                          data-testid="view-matches-button"
                        >
                          <Ship className="h-4 w-4 mr-2" />
                          View Matches
                        </Button>
                      ) : null}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Shortlist Tab */}
        <TabsContent value="shortlist" className="space-y-6">
          {shortlistedVessels.length === 0 ? (
            <Card>
              <CardContent className="py-16">
                <div className="text-center space-y-4">
                  <Star className="h-12 w-12 text-[hsl(var(--muted-foreground))] mx-auto" />
                  <h3 className="text-lg font-semibold">No Shortlisted Vessels</h3>
                  <p className="text-[hsl(var(--muted-foreground))] max-w-md mx-auto">
                    Shortlist vessels from your RFQ matches to compare and make decisions quickly.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {shortlistedVessels.map((match) => (
                <Card key={match.id} className="border-[hsl(var(--warning))]" data-testid={`shortlist-${match.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{match.vessel_name}</CardTitle>
                        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                          {match.rfq_details?.cargo_type} • {match.rfq_details?.load_port} → {match.rfq_details?.discharge_port}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Badge className="bg-[hsl(var(--accent))] text-white">
                          Fit {match.fit_score}%
                        </Badge>
                        <Star className="h-5 w-5 fill-[hsl(var(--warning))] text-[hsl(var(--warning))]" />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-[hsl(var(--muted-foreground))]">RFQ:</span>
                        <span className="ml-2 font-medium">{match.rfq_details?.rfq_number}</span>
                      </div>
                      <div>
                        <span className="text-[hsl(var(--muted-foreground))]">Distance:</span>
                        <span className="ml-2 font-medium">{match.distance_nm.toLocaleString()} nm</span>
                      </div>
                      <div>
                        <span className="text-[hsl(var(--muted-foreground))]">ETA:</span>
                        <span className="ml-2 font-medium">{match.eta_days.toFixed(1)} days</span>
                      </div>
                      <div>
                        <span className="text-[hsl(var(--muted-foreground))]">Freight:</span>
                        <span className="ml-2 font-medium">${(match.estimated_freight / 1000).toFixed(0)}K</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="text-sm font-semibold">Reasoning:</div>
                      <ul className="text-xs space-y-1 text-[hsl(var(--muted-foreground))]">
                        {match.reasoning.map((reason, idx) => (
                          <li key={idx}>• {reason}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button
                        className="flex-1 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"
                        onClick={() => createDealRoom(match.rfq_id, match.id)}
                        data-testid={`start-deal-${match.id}`}
                      >
                        <Handshake className="h-4 w-4 mr-2" />
                        Start Deal
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => toggleShortlist(match.id, false)}
                        data-testid={`remove-shortlist-${match.id}`}
                      >
                        Remove
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Matches Tab */}
        <TabsContent value="matches" className="space-y-6">
          {rfqs.filter(rfq => rfq.status !== 'draft').length === 0 ? (
            <Card>
              <CardContent className="py-16">
                <div className="text-center space-y-4">
                  <Ship className="h-12 w-12 text-[hsl(var(--muted-foreground))] mx-auto" />
                  <h3 className="text-lg font-semibold">Vessel Matching</h3>
                  <p className="text-[hsl(var(--muted-foreground))] max-w-md mx-auto">
                    Create an RFQ and click "Find Vessels" to see AI-powered vessel-cargo matches with fit scores.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">RFQs with Vessel Matches</h3>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  Click on an RFQ to view its vessel matches
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rfqs.filter(rfq => rfq.status !== 'draft').map((rfq) => (
                  <Card 
                    key={rfq.id} 
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => viewMatches(rfq.id)}
                    data-testid={`match-rfq-${rfq.id}`}
                  >
                    <CardHeader>
                      <CardTitle className="text-base">{rfq.cargo_type}</CardTitle>
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">
                        {rfq.load_port} → {rfq.discharge_port}
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-[hsl(var(--muted-foreground))]">Quantity:</span>
                        <span className="font-medium">{rfq.cargo_quantity_mt.toLocaleString()} MT</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[hsl(var(--muted-foreground))]">Vessel Class:</span>
                        <span className="font-medium">{rfq.vessel_class_required}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[hsl(var(--muted-foreground))]">Status:</span>
                        <Badge variant={rfq.status === 'matching' ? 'default' : 'secondary'}>
                          {rfq.status}
                        </Badge>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full mt-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          viewMatches(rfq.id);
                        }}
                      >
                        <Ship className="h-4 w-4 mr-2" />
                        View Matches
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Deal Rooms Tab */}
        <TabsContent value="deals" className="space-y-6">
          {dealRooms.length === 0 ? (
            <Card>
              <CardContent className="py-16">
                <div className="text-center space-y-4">
                  <Handshake className="h-12 w-12 text-[hsl(var(--muted-foreground))] mx-auto" />
                  <h3 className="text-lg font-semibold">No Active Deals</h3>
                  <p className="text-[hsl(var(--muted-foreground))] max-w-md mx-auto">
                    Create an RFQ, find matching vessels, and start a deal room to begin negotiations.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {dealRooms.map((deal) => (
                <Card 
                  key={deal.id} 
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => viewDealRoom(deal.id)}
                  data-testid={`deal-${deal.id}`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{deal.vessel_name}</CardTitle>
                        <p className="text-sm text-[hsl(var(--muted-foreground))]">
                          Deal Room
                        </p>
                      </div>
                      <Badge className={getStatusBadgeColor(deal.status)} data-testid="deal-status-badge">
                        {deal.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-[hsl(var(--secondary))] rounded-full h-2">
                        <div 
                          className="bg-[hsl(var(--accent))] h-2 rounded-full transition-all"
                          style={{ width: `${(deal.progress_stage / 4) * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-[hsl(var(--muted-foreground))]">
                        Stage {deal.progress_stage}/4
                      </span>
                    </div>

                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      Created {new Date(deal.created_at).toLocaleDateString()}
                    </div>

                    <Button variant="outline" size="sm" className="w-full" onClick={(e) => {
                      e.stopPropagation();
                      viewDealRoom(deal.id);
                    }}>
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Open Deal Room
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Matches Dialog */}
      <Dialog open={showMatchesDialog} onOpenChange={setShowMatchesDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Vessel Matches for {selectedRFQ?.cargo_type} ({matches.length} vessels)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {matches.map((match) => (
              <Card key={match.id} data-testid={`match-${match.id}`}>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-semibold">{match.vessel_name}</h3>
                        <p className="text-sm text-[hsl(var(--muted-foreground))]" style={{ fontFamily: 'var(--font-mono)' }}>
                          {match.vessel_imo} • {match.vessel_class}
                        </p>
                        {match.vessel_owner && (
                          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                            Owner: {match.vessel_owner}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className={`text-3xl font-semibold ${getFitScoreColor(match.fit_score)}`} style={{ fontFamily: 'var(--font-mono)' }}>
                          {match.fit_score}
                        </div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))]">Fit Score</div>
                      </div>
                    </div>

                    {/* Enhanced vessel details with Clarksons data */}
                    <div className="flex flex-wrap gap-2">
                      {match.vessel_flag && (
                        <Badge variant="outline" className="text-xs">
                          <Flag className="h-3 w-3 mr-1" />
                          {match.vessel_flag}
                        </Badge>
                      )}
                      {match.vessel_age !== null && match.vessel_age !== undefined && (
                        <Badge variant="outline" className="text-xs">
                          <Clock className="h-3 w-3 mr-1" />
                          {match.vessel_age} years old
                        </Badge>
                      )}
                      {match.scrubber_fitted && (
                        <Badge className="text-xs bg-[hsl(var(--success))] text-white">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          SOx Scrubber
                        </Badge>
                      )}
                      {match.vessel_builder && (
                        <Badge variant="outline" className="text-xs">
                          <Ship className="h-3 w-3 mr-1" />
                          {match.vessel_builder}
                        </Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-4 gap-4 p-4 rounded-lg bg-[hsl(var(--secondary))]">
                      <div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">DWT</div>
                        <div className="font-medium">{match.dwt.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">Distance</div>
                        <div className="font-medium">{match.distance_nm.toFixed(0)} nm</div>
                      </div>
                      <div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">ETA</div>
                        <div className="font-medium">{match.eta_days.toFixed(1)} days</div>
                      </div>
                      <div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">Est. Freight</div>
                        <div className="font-medium">${(match.estimated_freight_usd / 1000).toFixed(0)}K</div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold mb-2">Why This Works:</h4>
                      <ul className="space-y-1">
                        {match.reasoning.map((reason, idx) => (
                          <li key={idx} className="text-sm flex items-start gap-2">
                            <CheckCircle className="h-4 w-4 text-[hsl(var(--accent))] mt-0.5 flex-shrink-0" />
                            <span>{reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex gap-2 pt-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleShortlist(match.id, !match.shortlisted)}
                        className={match.shortlisted ? 'border-[hsl(var(--accent))] text-[hsl(var(--accent))]' : ''}
                      >
                        {match.shortlisted ? 'Shortlisted ✓' : 'Add to Shortlist'}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => createDealRoom(selectedRFQ.id, match.id)}
                        disabled={loading}
                        className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                        data-testid="start-deal-button"
                      >
                        <Handshake className="h-4 w-4 mr-2" />
                        Start Deal
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Deal Room Dialog */}
      <Dialog open={showDealDialog} onOpenChange={setShowDealDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>
              Deal Room: {selectedDeal?.vessel_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Progress Stages */}
            <div className="flex items-center justify-between text-xs">
              {['Negotiation', 'Terms', 'Compliance', 'Closing'].map((stage, idx) => (
                <div key={idx} className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    selectedDeal && idx < selectedDeal.progress_stage 
                      ? 'bg-[hsl(var(--accent))] text-white'
                      : 'bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]'
                  }`}>
                    {idx + 1}
                  </div>
                  <span className="mt-1">{stage}</span>
                </div>
              ))}
            </div>

            {/* Messages */}
            <div className="border rounded-lg p-4 h-64 overflow-y-auto space-y-3 bg-[hsl(var(--secondary))]/30">
              {dealMessages.map((msg) => (
                <div key={msg.id} className={msg.message_type === 'system' ? 'text-center' : ''}>
                  {msg.message_type === 'system' ? (
                    <div className="text-xs text-[hsl(var(--muted-foreground))] italic">
                      {msg.message}
                    </div>
                  ) : (
                    <div className="bg-[hsl(var(--card))] rounded-lg p-3 border">
                      <div className="text-xs font-semibold mb-1">{msg.user_name}</div>
                      <div className="text-sm">{msg.message}</div>
                      <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                        {new Date(msg.created_at).toLocaleTimeString()}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Message Input */}
            <div className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                data-testid="message-input"
              />
              <Button onClick={sendMessage} data-testid="send-message-button">
                <Send className="h-4 w-4" />
              </Button>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t">
              {selectedDeal && selectedDeal.progress_stage < 4 && (
                <Button
                  onClick={() => {
                    const nextStage = selectedDeal.progress_stage + 1;
                    const nextStatus = getStatusFromStage(nextStage);
                    updateDealStatus(selectedDeal.id, nextStatus, nextStage);
                  }}
                  className="flex-1"
                  data-testid="next-stage-button"
                >
                  Move to Next Stage
                </Button>
              )}
              {selectedDeal && selectedDeal.progress_stage === 4 && selectedDeal.status !== 'completed' && (
                <Button
                  onClick={() => updateDealStatus(selectedDeal.id, 'completed', 4)}
                  className="flex-1 bg-[hsl(var(--success))] text-white hover:bg-[hsl(var(--success))]/90"
                  data-testid="complete-deal-button"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Complete Deal
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Conversational Sidebar */}
      <ConversationalSidebar 
        module="marketplace"
        context={{
          rfqs: rfqs,
          matches: matches,
          dealRooms: dealRooms
        }}
        onAction={(type, params) => {
          console.log('[Marketplace] AI Action:', type, params);
          // Actions can be implemented: create RFQ, find matches, start deal, etc.
        }}
      />
    </div>
  );
}
